// AdvertisingPanel.jsx - Launch marketing campaigns

import React, { useState } from 'react';
import { CAMPAIGN_TYPES } from '../../systems/AdvertisingSystem.js';
import { debug } from '../../lib/debug.js';

const REGIONS = {
  PACIFIC_NORTHWEST: { name: 'Pacific Northwest', airports: ['KPDX', 'KSEA', 'KGEG', 'KEUG', 'KMFR', 'KBOI', 'KRDM'] },
  CALIFORNIA: { name: 'California', airports: ['KSFO', 'KOAK', 'KSJC', 'KSMF', 'KLAX', 'KSAN'] },
  MOUNTAIN: { name: 'Mountain West', airports: ['KBZN', 'KJAC', 'KIDA', 'KSLC', 'KRNO', 'KDEN', 'KCOS', 'KASE'] },
  SOUTHWEST: { name: 'Southwest', airports: ['KPHX', 'KLAS'] },
  MIDWEST: { name: 'Midwest', airports: ['KORD', 'KMSP', 'KDTW'] },
  SOUTH: { name: 'South', airports: ['KATL', 'KBNA', 'KCLT'] },
  ALASKA: { name: 'Alaska', airports: ['PANC', 'PAFA', 'PADQ'] },
};

export function AdvertisingPanel({ advertisingState, cash, slots, onLaunch, onCancel, onClose }) {
  const [selectedType, setSelectedType] = useState(null);
  const [selectedTarget, setSelectedTarget] = useState(null);

  const activeCampaigns = advertisingState?.activeCampaigns || [];
  const playerAirports = Object.keys(slots || {});

  debug('[AdvertisingPanel] Props:', { cash, activeCampaigns: activeCampaigns.length, playerAirports });

  const handleLaunch = () => {
    if (!selectedType) return;
    const template = CAMPAIGN_TYPES[selectedType];

    // Validate target for campaigns that need one
    if (template.effects.scope === 'single' && !selectedTarget) {
      alert('Select a target airport');
      return;
    }
    if (template.effects.scope === 'region' && !selectedTarget) {
      alert('Select a target region');
      return;
    }

    debug('[AdvertisingPanel] Launching:', { type: selectedType, target: selectedTarget });
    onLaunch(selectedType, selectedTarget);
    setSelectedType(null);
    setSelectedTarget(null);
  };

  const canAfford = (type) => cash >= CAMPAIGN_TYPES[type].costPerMonth;

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>

        <div style={styles.header}>
          <div style={styles.headerTitle}>ADVERTISING</div>
          <button style={styles.closeButton} onClick={onClose}>X</button>
        </div>

        {/* Active Campaigns */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>ACTIVE CAMPAIGNS</div>
          {activeCampaigns.length === 0 ? (
            <div style={styles.emptyState}>No active campaigns</div>
          ) : (
            <div style={styles.grid}>
              {activeCampaigns.map(c => (
                <div key={c.id} style={styles.activeCard}>
                  <div style={styles.cardTop}>
                    <div style={styles.name}>{c.name}</div>
                    <div style={styles.target}>{c.target || 'All Markets'}</div>
                  </div>
                  <div style={styles.stats}>
                    <span>{c.monthsRemaining} months remaining</span>
                    <span>${formatMoney(c.costPerMonth)}/mo</span>
                  </div>
                  <button style={styles.cancelBtn} onClick={() => onCancel(c.id)}>
                    Cancel
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Monthly Summary */}
        {advertisingState?.totalMonthlySpend > 0 && (
          <div style={styles.summary}>
            Total ad spend: ${formatMoney(advertisingState.totalMonthlySpend)}/month
          </div>
        )}

        {/* Launch New Campaign */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>LAUNCH CAMPAIGN</div>
          <div style={styles.campaignList}>
            {Object.entries(CAMPAIGN_TYPES).map(([key, campaign]) => {
              const affordable = canAfford(key);
              const isSelected = selectedType === key;

              return (
                <div
                  key={key}
                  style={{
                    ...styles.campaignCard,
                    ...(isSelected ? styles.selected : {}),
                    ...(!affordable ? styles.locked : {}),
                  }}
                  onClick={() => affordable && setSelectedType(key)}
                >
                  <div style={styles.campaignHeader}>
                    <div style={styles.campaignName}>{campaign.name}</div>
                    <div style={styles.campaignCost}>${formatMoney(campaign.costPerMonth)}/mo</div>
                  </div>
                  <div style={styles.campaignDesc}>{campaign.description}</div>
                  <div style={styles.campaignEffects}>
                    {campaign.effects.demandBoost && <span>+{Math.round(campaign.effects.demandBoost * 100)}% demand</span>}
                    {campaign.effects.relationsGain && <span>+{campaign.effects.relationsGain} relations</span>}
                    {campaign.effects.reputationBoost && <span>+{campaign.effects.reputationBoost} rep</span>}
                  </div>
                  <div style={styles.campaignDuration}>{campaign.duration} month minimum</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Target Selection */}
        {selectedType && (
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              {CAMPAIGN_TYPES[selectedType].effects.scope === 'single' ? 'SELECT TARGET AIRPORT' :
               CAMPAIGN_TYPES[selectedType].effects.scope === 'region' ? 'SELECT TARGET REGION' :
               'TARGET: ALL MARKETS'}
            </div>

            {CAMPAIGN_TYPES[selectedType].effects.scope === 'single' && (
              <div style={styles.targetGrid}>
                {playerAirports.map(code => (
                  <button
                    key={code}
                    style={{
                      ...styles.targetBtn,
                      ...(selectedTarget === code ? styles.targetSelected : {}),
                    }}
                    onClick={() => setSelectedTarget(code)}
                  >
                    {code}
                  </button>
                ))}
              </div>
            )}

            {CAMPAIGN_TYPES[selectedType].effects.scope === 'region' && (
              <div style={styles.targetGrid}>
                {Object.entries(REGIONS).map(([key, region]) => (
                  <button
                    key={key}
                    style={{
                      ...styles.targetBtn,
                      ...(selectedTarget === key ? styles.targetSelected : {}),
                    }}
                    onClick={() => setSelectedTarget(key)}
                  >
                    {region.name}
                  </button>
                ))}
              </div>
            )}

            <button
              style={styles.launchBtn}
              onClick={handleLaunch}
              disabled={CAMPAIGN_TYPES[selectedType].effects.scope === 'single' && !selectedTarget}
            >
              Launch {CAMPAIGN_TYPES[selectedType].name} - ${formatMoney(CAMPAIGN_TYPES[selectedType].costPerMonth)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function formatMoney(cents) {
  if (!cents) return '0';
  if (cents >= 100000000) return `${(cents / 100000000).toFixed(1)}M`;
  if (cents >= 100000) return `${(cents / 100000).toFixed(0)}K`;
  return (cents / 100).toLocaleString();
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },
  panel: {
    width: '700px', maxHeight: '90vh', overflow: 'auto',
    backgroundColor: '#0a0a0a', border: '1px solid #222', borderRadius: '8px',
    fontFamily: 'inherit',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px', borderBottom: '1px solid #222',
  },
  headerTitle: {
    fontSize: '13px', fontWeight: '600', letterSpacing: '2px', color: '#fff',
  },
  closeButton: {
    background: 'none', border: 'none', color: '#666', fontSize: '24px', cursor: 'pointer',
  },
  section: { padding: '20px' },
  sectionTitle: {
    fontSize: '10px', letterSpacing: '2px', color: '#666', marginBottom: '12px',
  },
  emptyState: { color: '#444', fontSize: '12px', fontStyle: 'italic' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' },
  summary: {
    padding: '12px 20px', backgroundColor: '#111', borderTop: '1px solid #1a1a1a',
    fontSize: '11px', color: '#888',
  },
  activeCard: {
    backgroundColor: '#111', border: '1px solid #333', borderRadius: '6px', padding: '14px',
  },
  cardTop: { marginBottom: '10px' },
  name: { fontSize: '13px', fontWeight: '600', color: '#fff' },
  target: { fontSize: '11px', color: '#00ff88', marginTop: '2px' },
  stats: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: '10px', color: '#888', marginBottom: '10px',
  },
  cancelBtn: {
    background: 'none', border: '1px solid #661111', borderRadius: '3px',
    padding: '4px 10px', fontSize: '9px', color: '#ff4444', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  campaignList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  campaignCard: {
    backgroundColor: '#111', border: '1px solid #222', borderRadius: '6px', padding: '14px',
    cursor: 'pointer', transition: 'all 0.15s',
  },
  selected: { borderColor: '#00ff88', backgroundColor: '#0a1a0f' },
  locked: { opacity: 0.5, cursor: 'not-allowed' },
  campaignHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px',
  },
  campaignName: { fontSize: '13px', fontWeight: '600', color: '#fff' },
  campaignCost: { fontSize: '12px', color: '#00ff88' },
  campaignDesc: { fontSize: '11px', color: '#666', marginBottom: '8px' },
  campaignEffects: {
    display: 'flex', gap: '12px', fontSize: '10px', color: '#888',
  },
  campaignDuration: { fontSize: '9px', color: '#444', marginTop: '6px' },
  targetGrid: {
    display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px',
  },
  targetBtn: {
    background: 'none', border: '1px solid #333', borderRadius: '3px',
    padding: '8px 14px', fontSize: '11px', color: '#888', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  targetSelected: { borderColor: '#00ff88', color: '#00ff88' },
  launchBtn: {
    width: '100%', padding: '12px',
    background: 'linear-gradient(135deg, #00ff88, #00cc6a)',
    border: 'none', borderRadius: '4px',
    fontSize: '12px', fontWeight: '600', color: '#000', cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

export default AdvertisingPanel;
